import { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Check, ChevronDown, X } from "lucide-react";
import { CORE_EXPERTISE_OPTIONS, normaliseExpertiseList } from "../lib/clinicalOptions";

export const SPECIALTY_OPTIONS = [
  ...CORE_EXPERTISE_OPTIONS,
  "Clinical Psychology", "Counselling Psychology", "Trauma & PTSD",
  "Anxiety", "Depression & Mood", "ADHD", "Autism / Neurodiversity", "Eating Disorders",
  "Addiction & Substance Use", "Grief & Loss", "OCD",
  "Psychedelic-assisted therapies",
  "Perinatal / Postnatal",
  "Sleep", "Chronic Pain", "Workplace & Stress",
];

export const LANGUAGE_OPTIONS = [
  "English", "Arabic", "Cantonese", "French", "German", "Greek", "Hebrew", "Hindi",
  "Italian", "Japanese", "Korean", "Mandarin", "Persian / Farsi", "Punjabi", "Russian",
  "Spanish", "Tamil", "Turkish", "Urdu", "Vietnamese", "Auslan",
];

const SPECIALTY_OPTIONS_BY_PROFESSION = {
  Psychologist: SPECIALTY_OPTIONS,
  Psychiatrist: SPECIALTY_OPTIONS.filter(
    (option) => !["Clinical Psychology", "Counselling Psychology", "Relationships & Family", "Workplace & Stress"].includes(option)
  ),
};

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Multi-select dropdown — value/onChange use a comma-separated string for DB compatibility.
export const SpecialtyMultiSelect = ({
  value = "",
  onChange,
  profession = "",
  testId = "clinician-signup-specialty",
  optionsOverride = null,
  approvedEndpoint = "/public/specialties",
  resultKey = "specialties",
  otherLabel = "Other expertise or specialty",
  inputPlaceholder = "Enter expertise or specialty",
  emptyLabel = "Tick any that apply",
}) => {
  const [approvedOptions, setApprovedOptions] = useState([]);
  const [showOther, setShowOther] = useState(false);
  const [otherValue, setOtherValue] = useState("");
  const otherInputRef = useRef(null);
  const selected = useMemo(
    () => (value ? value.split(",").map((s) => s.trim()).filter(Boolean) : []),
    [value]
  );
  useEffect(() => {
    if (!showOther) return;
    requestAnimationFrame(() => {
      otherInputRef.current?.scrollIntoView({ block: "nearest" });
      otherInputRef.current?.focus();
    });
  }, [showOther]);

  useEffect(() => {
    let active = true;
    if (!approvedEndpoint) return undefined;
    const query = profession ? "?profession=" + encodeURIComponent(profession) : "";
    fetch(API + approvedEndpoint + query)
      .then((response) => (response.ok ? response.json() : { [resultKey]: [] }))
      .then((data) => {
        if (active) setApprovedOptions(Array.isArray(data[resultKey]) ? data[resultKey] : []);
      })
      .catch(() => {
        if (active) setApprovedOptions([]);
      });
    return () => { active = false; };
  }, [profession, approvedEndpoint, resultKey]);

  const options = useMemo(() => {
    const defaults = optionsOverride || SPECIALTY_OPTIONS_BY_PROFESSION[profession] || SPECIALTY_OPTIONS;
    return normaliseExpertiseList([...defaults, ...approvedOptions, ...selected]).sort((a, b) => a.localeCompare(b));
  }, [profession, optionsOverride, approvedOptions, selected]);

  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt];
    onChange(next.join(", "));
  };

  const addOther = () => {
    const custom = otherValue.trim();
    if (!custom) return;
    if (!selected.some((item) => item.toLowerCase() === custom.toLowerCase())) {
      onChange([...selected, custom].join(", "));
    }
    setOtherValue("");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId + "-trigger"}
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <span className={selected.length ? "truncate text-slate-900" : "text-slate-400"}>
            {selected.length ? selected.length + " selected" : emptyLabel}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="max-h-72 w-[var(--radix-popover-trigger-width)] overflow-y-auto bg-white p-1" align="start">
        {options.map((opt) => {
          const checked = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              data-testid={testId + "-opt-" + slug(opt)}
              className="flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-left text-sm hover:bg-slate-100"
            >
              <span
                className={"flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border " + (checked ? "border-primary bg-primary text-white" : "border-slate-300 bg-white")}
                aria-hidden="true"
              >
                {checked && <Check className="h-3 w-3" />}
              </span>
              <span className="flex-1 text-slate-700">{opt}</span>
            </button>
          );
        })}
        <div className="mt-1 border-t border-slate-200 pt-1">
          <button
            type="button"
            onClick={() => setShowOther((current) => !current)}
            data-testid={testId + "-other-toggle"}
            className={"flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-left text-sm font-medium text-primary hover:bg-sky-50 " + (showOther ? "bg-sky-50" : "")}
          >
            <span
              className={"flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border " + (showOther ? "border-primary bg-primary text-white" : "border-slate-300 bg-white")}
              aria-hidden="true"
            >
              {showOther && <Check className="h-3 w-3" />}
            </span>
            {otherLabel}
          </button>
          {showOther && (
            <div className="flex gap-2 p-2">
              <input
                ref={otherInputRef}
                value={otherValue}
                onChange={(event) => setOtherValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addOther();
                  }
                }}
                placeholder={inputPlaceholder}
                data-testid={testId + "-other-input"}
                className="h-9 min-w-0 flex-1 rounded-md border border-input px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
              <button
                type="button"
                onClick={addOther}
                className="h-9 rounded-md bg-primary px-3 text-sm font-medium text-white"
              >
                Add
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const SpecialtyChips = ({ value = "", onRemove, testIdPrefix = "specialty-chip-" }) => {
  const selected = value ? value.split(",").map((s) => s.trim()).filter(Boolean) : [];
  if (!selected.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {selected.map((s) => (
        <span key={s} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700" data-testid={testIdPrefix + s}>
          {s}
          {onRemove && (
            <button type="button" onClick={() => onRemove(s)} className="text-slate-400 hover:text-slate-700" aria-label={"Remove " + s}>
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
};

export const LanguageMultiSelect = (props) => (
  <SpecialtyMultiSelect
    {...props}
    optionsOverride={LANGUAGE_OPTIONS}
    approvedEndpoint="/public/languages"
    resultKey="languages"
    otherLabel="Other language"
    inputPlaceholder="Enter a language"
    emptyLabel="Select languages"
  />
);

export const LanguageChips = (props) => (
  <SpecialtyChips {...props} testIdPrefix="language-chip-" />
);
